import React from 'react';
import {
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
  AgnesButton,
  AgnesDateRangePickerInput,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { Filter, Trash2, X } from 'lucide-react';
import type { AfastamentoFiltros } from '@/lib/afastamento';
import type { OpcoesFiltro } from '@/stores/useAfastamentoStore';
import { labelFiltro, protegerSelectDoFechamento } from '@/lib/filtros';

// estado interno do popover: um valor por campo (single-select). Vira lista no submit.
type FiltroForm = {
  cnpj: string;
  periodo: string[];
  regime: string;
  categoria: string;
  motivo: string;
  cargo: string;
  cbo: string;
};

const VAZIO: FiltroForm = {
  cnpj: '',
  periodo: [],
  regime: '',
  categoria: '',
  motivo: '',
  cargo: '',
  cbo: '',
};

const TRIGGER_CLS =
  'w-full min-w-0 [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

// string[] de datas ISO -> {startDate, endDate}. Vazio/parcial -> undefined.
function parsePeriodo(periodo: string[]): { startDate?: string; endDate?: string } {
  if (!periodo || periodo.length === 0) return {};
  return { startDate: periodo[0], endDate: periodo[periodo.length - 1] };
}

function toFiltros(f: FiltroForm): AfastamentoFiltros {
  const one = (v: string) => (v ? [v] : []);
  const oneNum = (v: string) => (v ? [Number(v)] : []);
  return {
    cnpjList: one(f.cnpj),
    regimeList: one(f.regime),
    categoriaList: oneNum(f.categoria),
    motivoList: oneNum(f.motivo),
    cargoList: one(f.cargo),
    cboList: one(f.cbo),
    ...parsePeriodo(f.periodo),
  };
}

type Props = {
  opcoes: OpcoesFiltro;
  onApply: (f: AfastamentoFiltros) => void;
  count?: number;
};

type SelectFieldProps = {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
};
function SelectField({ value, onValueChange, placeholder, options }: SelectFieldProps) {
  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <AgnesSelect value={value} onValueChange={onValueChange}>
      <AgnesSelectTrigger className={TRIGGER_CLS}>
        <AgnesSelectValue placeholder={placeholder} title={selectedLabel} />
      </AgnesSelectTrigger>
      <AgnesSelectContent
        position="popper"
        className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)]"
      >
        {options.map((o) => (
          // o ItemText do DS é o último span do item (flex): sem min-w-0 ele não encolhe
          // e o truncate do label não aplica.
          <AgnesSelectItem key={o.value} value={o.value} className="[&>span:last-child]:min-w-0">
            <span className="block w-full min-w-0 truncate" title={o.label}>
              {o.label}
            </span>
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );
}

export default function AfastamentoFilter({ opcoes, onApply, count = 0 }: Props) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState<FiltroForm>(VAZIO);
  const set = (k: keyof FiltroForm) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const aplicar = () => {
    onApply(toFiltros(f));
    setOpen(false);
  };
  const limpar = () => {
    setF(VAZIO);
    onApply(toFiltros(VAZIO));
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          aria-label={count > 0 ? `Filtro, ${count} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', count)}
        </AgnesButton>
      </AgnesPopoverTrigger>

      <AgnesPopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-24px)] p-0"
        dataQa="afastamento-filter-popover"
        onInteractOutside={protegerSelectDoFechamento}
      >
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-w-0 max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4">
          {opcoes.cnpj.length > 0 && (
            <SelectField
              value={f.cnpj}
              onValueChange={set('cnpj')}
              placeholder="CNPJ"
              options={opcoes.cnpj}
            />
          )}
          <AgnesDateRangePickerInput
            value={f.periodo}
            onChange={(dates) => setF((s) => ({ ...s, periodo: dates }))}
            label="Período"
          />
          <SelectField
            value={f.regime}
            onValueChange={set('regime')}
            placeholder="Regime previdenciário"
            options={opcoes.regime}
          />
          <SelectField
            value={f.categoria}
            onValueChange={set('categoria')}
            placeholder="Categoria"
            options={opcoes.categoria}
          />
          <SelectField
            value={f.motivo}
            onValueChange={set('motivo')}
            placeholder="Motivo de afastamento"
            options={opcoes.motivo}
          />
          <SelectField
            value={f.cargo}
            onValueChange={set('cargo')}
            placeholder="Cargo"
            options={opcoes.cargo}
          />
          <SelectField
            value={f.cbo}
            onValueChange={set('cbo')}
            placeholder="CBO"
            options={opcoes.cbo}
          />
        </div>

        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
